import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Landing2Layout from './layout/Landing2Layout';
import Sym from './components/Sym';
import ILLUSTRATIONS from './sections2/FeatureIllustrations';
import authService from '../../utils/authService';

// Animación del portal (la misma de la tarjeta "Portal 24/7") para el panel de login.
const PortalFeed = ILLUSTRATIONS.smartphone;

/**
 * Login con el look del rediseño. Reutiliza authService.login (mismo core que el
 * login de la app: rate-limit interno, redirect por rol, must_change_password).
 * El panel izquierdo es de marca — SIN testimonio ficticio (decisión §4.7).
 */
const ROLE_ROUTES = {
  administrator: '/app/system-analytics',
  company: '/app/company-dashboard',
  employee: '/app/employee-payroll-portal',
};

const inputStyle = {
  width: '100%', background: 'var(--surface-2)', border: '1px solid var(--border-2)', borderRadius: 12,
  padding: '14px 16px', color: 'var(--heading)', font: "500 1rem 'Hanken Grotesk'", outline: 'none',
};
const labelStyle = { font: "600 .9rem 'Hanken Grotesk'", color: 'var(--body-strong)', marginBottom: 8, display: 'block' };

const VALUES = [
  { icon: 'cloud_download', t: 'Tus recibos, directo del SAT' },
  { icon: 'smartphone', t: 'Cada quien descarga el suyo' },
  { icon: 'lock', t: 'Tu e.firma, cifrada y aislada' },
];

const Login2 = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ identifier: '', password: '' });
  const [state, setState] = useState('idle'); // idle | busy
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  // Si ya hay sesión, entra directo al portal según rol.
  useEffect(() => {
    if (authService?.isAuthenticated?.()) {
      const user = authService?.getCurrentUser?.();
      navigate(ROLE_ROUTES[user?.role] || '/app/system-analytics', { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (e) => {
    e.preventDefault();
    if (state === 'busy') return;
    if (!form.identifier.trim() || !form.password) {
      setError('Escribe tu correo y tu contraseña.');
      return;
    }
    setState('busy');
    setError(null);
    try {
      const result = await authService.login(form.identifier.trim(), form.password);
      if (result?.user?.must_change_password === true) {
        navigate('/mandatory-password-change', { replace: true });
        return;
      }
      const route = result?.redirectRoute || ROLE_ROUTES[result?.user?.role] || '/app/system-analytics';
      navigate(route, { replace: true });
    } catch (err) {
      setError(err?.message || 'No pudimos iniciar sesión. Revisa tus datos e inténtalo de nuevo.');
      setState('idle');
    }
  };

  return (
    <Landing2Layout title="Iniciar sesión — PaySlip">
      <section style={{ position: 'relative', overflow: 'clip', minHeight: 'calc(100vh - 74px)', display: 'flex', alignItems: 'center', padding: 'clamp(32px,5vw,64px) clamp(20px,5vw,40px)' }}>
        <div aria-hidden style={{ position: 'absolute', top: '10%', left: -80, width: 420, height: 420, borderRadius: '50%', background: 'radial-gradient(circle,rgba(47,107,255,.22),transparent 65%)', filter: 'blur(18px)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', maxWidth: 960, width: '100%', margin: '0 auto', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 26, overflow: 'hidden', display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', boxShadow: '0 40px 80px -40px rgba(0,0,0,.8)' }}>
          {/* Panel de marca */}
          <div data-dark style={{ position: 'relative', overflow: 'clip', background: 'linear-gradient(160deg,#12224A,#00173D)', padding: 'clamp(32px,4vw,48px)', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 'clamp(20px,4vw,38px)', minHeight: 420 }}>
            <div style={{ position: 'absolute', bottom: -100, right: -60, width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle,rgba(47,107,255,.35),transparent 65%)', filter: 'blur(10px)' }} />
            {/* Animación del portal (reutilizada de las tarjetas), en vez del logo duplicado */}
            <div style={{ position: 'relative', height: 200 }}>
              <PortalFeed />
            </div>
            <div style={{ position: 'relative' }}>
              <p style={{ font: "500 1.5rem/1.4 'Source Serif 4'", color: '#EAF0FF', margin: '0 0 24px' }}>
                Tus recibos de nómina, entregados <span style={{ fontStyle: 'italic', color: '#8FB4FF' }}>solos</span>.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {VALUES.map((v) => (
                  <div key={v.t} style={{ display: 'flex', alignItems: 'center', gap: 10, font: "600 .95rem 'Hanken Grotesk'", color: '#C7D3EC' }}>
                    <Sym name={v.icon} size={19} color="#8FB4FF" />{v.t}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Formulario */}
          <div style={{ padding: 'clamp(32px,4vw,48px)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <h1 style={{ font: "600 clamp(1.7rem,3.5vw,2.3rem) 'Source Serif 4'", color: 'var(--heading)', margin: '0 0 6px' }}>Inicia sesión</h1>
            <p style={{ font: "400 1rem 'Hanken Grotesk'", color: 'var(--muted)', margin: '0 0 26px' }}>Consulta y entrega los recibos de tu empresa.</p>
            <form onSubmit={onSubmit}>
              <div style={{ display: 'grid', gap: 16 }}>
                <div>
                  <label style={labelStyle}>Correo</label>
                  <input type="email" value={form.identifier} onChange={set('identifier')} placeholder="tu@empresa.mx" style={inputStyle} autoComplete="username" />
                </div>
                <div>
                  <label style={labelStyle}>Contraseña</label>
                  <input type="password" value={form.password} onChange={set('password')} placeholder="••••••••" style={inputStyle} autoComplete="current-password" />
                </div>
                <a onClick={() => navigate('/contact')} style={{ font: "600 .9rem 'Hanken Grotesk'", color: '#6FA0FF', cursor: 'pointer', justifySelf: 'end' }}>¿Olvidaste tu contraseña?</a>
                {error && (
                  <div style={{ font: "500 .9rem 'Hanken Grotesk'", color: '#F87171', background: 'rgba(248,113,113,.1)', border: '1px solid rgba(248,113,113,.3)', borderRadius: 10, padding: '10px 14px' }}>{error}</div>
                )}
                <button type="submit" disabled={state === 'busy'}
                  style={{ width: '100%', padding: 16, font: "700 1.05rem 'IBM Plex Mono'", color: '#fff', background: '#2F6BFF', border: 'none', borderRadius: 13, cursor: state === 'busy' ? 'default' : 'pointer', boxShadow: '0 12px 30px -12px rgba(47,107,255,.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9 }}>
                  {state === 'busy'
                    ? <><span style={{ width: 19, height: 19, border: '2.5px solid rgba(255,255,255,.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'ls2-spin .7s linear infinite' }} />Entrando…</>
                    : <>Iniciar sesión<Sym name="arrow_forward" size={20} /></>}
                </button>
              </div>
            </form>
            <p style={{ font: "400 .95rem 'Hanken Grotesk'", color: 'var(--muted)', margin: '24px 0 0', textAlign: 'center' }}>
              ¿No tienes cuenta? <a onClick={() => navigate('/register')} style={{ fontWeight: 700, color: '#6FA0FF', cursor: 'pointer' }}>Prueba 15 días</a>
            </p>
          </div>
        </div>
      </section>
    </Landing2Layout>
  );
};

export default Login2;
